import { Children, type ReactNode } from 'react'
import List from '@mui/material/List'

type Props = {
  /** The height of one row, in px. */
  rowHeight: number
  children: ReactNode
} & (
  | {
      /** The list is this many rows high at most, and scrolls past them. */
      rows: number
      minRows?: never
    }
  | {
      /**
       * In place of `rows`: the list is a flex item that shrinks to the space its column has, and
       * not below this many rows. Its parent is a flex column of a fixed height. A list of no more
       * rows than this does not shrink: a minimum from the rounded `rowHeight` could cut its last
       * row by a pixel.
       */
      minRows: number
      rows?: never
    }
)

/**
 * A list of rows in a frame that scrolls. Without the frame, a row cut at the bottom edge looks
 * like a defect; in a frame, it reads as "scroll for more". A short list takes only the height of
 * its rows.
 *
 * ⚠ A `div`, not a `ul`: the rows are links and stacks, not `li` items, and a `ul` without them is
 * a list of no items for a screen reader.
 */
export default function ScrollList({ rowHeight, rows, minRows, children }: Readonly<Props>) {
  return (
    <List
      component="div"
      disablePadding
      sx={({ palette, shape }) => ({
        ...(minRows === undefined
          ? { maxHeight: rows * rowHeight }
          : Children.count(children) <= minRows
            ? { flexShrink: 0 }
            : { flex: '0 1 auto', minHeight: minRows * rowHeight }),
        overflowY: 'auto',
        border: '1px solid',
        borderColor: palette.divider,
        borderRadius: `${shape.borderRadius}px`,
        // ⚠ The theme sets a row 16 px out on each side, past the text column. In a frame that
        // cuts the row and makes the list scroll sideways, so here a row stays inside.
        '& .MuiListItemButton-root': { marginX: 0 },
      })}
    >
      {children}
    </List>
  )
}
